import type { ReactElement } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "../lib/utils";
import { TermsModal } from "./TermsModal";

/** Platform-tier fallback for the Powered by attribution. Used when
 *  no closer ancestor in the brand cascade has set
 *  ``powered_by_text``. The platform partner is "Finterra" by the
 *  locked tenancy model (2026-04-21). */
const POWERED_BY_PLATFORM_FALLBACK = "Finterra";

export interface FooterProps {
  /** Merged brand_config from the API. Reads:
   *
   *   - ``terms_url``           - link target for the T&Cs link.
   *   - ``privacy_url``         - link target for the Privacy link.
   *     Optional - hidden when not set (no default privacy doc).
   *   - ``copyright_text``      - explicit override; falls back to a
   *     "© {year} {product_name}" auto-string.
   *   - ``product_name``        - used in the auto copyright string.
   *   - ``powered_by_text``     - closer ancestor's chosen Powered by
   *     name. When unset, falls back to the platform name. (#159) */
  readonly brand: Record<string, unknown> | undefined | null;
  readonly className?: string;
}

/** App footer - copyright (left), T&Cs link + Powered by attribution
 *  (right). Mirrors the V1 portal footer the operator already
 *  recognizes. Renders nothing when brand isn't loaded so we don't
 *  flash an empty bar before the profile query lands.
 *
 *  Powered-by cascade (per user 2026-04-27): the brand cascade merges
 *  partner → org → location with closer scopes winning. If no
 *  ancestor in that chain has ``powered_by_text`` set, fall back to
 *  the platform partner's name (currently "Finterra") so the
 *  attribution always shows. The previous implementation hid the
 *  right side entirely when no level set the flag - that left every
 *  in-tree partner / org with no Powered by until they explicitly
 *  set a knob, which isn't what was asked for.
 *
 *  T&Cs link still requires ``terms_url`` because there's no
 *  meaningful default URL to link to - hidden when absent. */
export function Footer({ brand, className }: FooterProps) {
  const { t } = useTranslation();
  const [showDefaultTerms, setShowDefaultTerms] = useState(false);
  if (!brand) return null;

  const productName = pickStr(brand, "product_name");
  const explicitCopyright = pickStr(brand, "copyright_text");
  const termsUrl = pickStr(brand, "terms_url");
  const privacyUrl = pickStr(brand, "privacy_url");
  const poweredByText = pickStr(brand, "powered_by_text") ?? POWERED_BY_PLATFORM_FALLBACK;

  // Auto-fallback copyright: "© {year} {productName}" when the brand
  // doesn't set a custom string. Year is the user's local year -
  // good enough for a footer that doesn't carry billing weight.
  const year = new Date().getFullYear();
  const copyrightLine = explicitCopyright ?? `© ${year}${productName ? ` ${productName}` : ""}`;

  // T&Cs link target. When the brand sets a ``terms_url``, the link
  // opens it in a new tab (per-partner doc). When it doesn't, the
  // link opens the V1-baseline T&C content in a modal so cashiers
  // always have something to read. Same approach V1 used in the
  // DCPOS-Portal default layout.
  const renderTerms = (): ReactElement =>
    termsUrl ? (
      <a
        href={termsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:text-foreground hover:underline"
      >
        {t("footer.terms")}
      </a>
    ) : (
      <button
        type="button"
        onClick={() => setShowDefaultTerms(true)}
        className="hover:text-foreground hover:underline"
      >
        {t("footer.terms")}
      </button>
    );

  return (
    <>
      <footer
        className={cn(
          // Slim utility strip - py-1.5 keeps the footer to a single
          // line at all widths so it doesn't crowd the page bottom
          // (2026-05-03 dogfood: "see large footer").
          "border-t border-line bg-surface/60 px-4 py-1.5 text-[0.6875rem] text-faint",
          "flex flex-col items-center justify-between gap-1 sm:flex-row",
          className,
        )}
      >
        <span className="tracking-wide">{copyrightLine}</span>
        <span className="flex items-center gap-2">
          {renderTerms()}
          {privacyUrl && (
            <>
              <span className="text-faint" aria-hidden="true">
                |
              </span>
              <a
                href={privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground hover:underline"
              >
                {t("footer.privacy")}
              </a>
            </>
          )}
          <span className="text-faint" aria-hidden="true">
            |
          </span>
          <span className="uppercase tracking-wide">
            {t("footer.poweredBy")} <span className="font-semibold">{poweredByText}</span>
          </span>
        </span>
      </footer>
      <TermsModal
        open={showDefaultTerms}
        onClose={() => setShowDefaultTerms(false)}
        partnerName={poweredByText}
      />
    </>
  );
}

function pickStr(brand: Record<string, unknown>, key: string): string | undefined {
  const v = brand[key];
  return typeof v === "string" && v.trim() ? v : undefined;
}
